/**
 * Collective marketing page. The shared Mamahood marketing header (mh-*) is rendered once by
 * TwentyTwentysixSiteLayout alongside Home and other twentytwentysix routes — see MamahoodMarketingHeader.jsx.
 */
import React, { useCallback, useId, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "../styles/MamahoodCollectivePageTwo.css";
import "../styles/MamahoodHeroTitle.css";

function ExpandableReviewBody({ body }) {
  const bodyId = useId();
  const [expanded, setExpanded] = useState(false);
  const [showReadMore, setShowReadMore] = useState(false);
  const textRef = useRef(null);

  const measureOverflow = useCallback(() => {
    const el = textRef.current;
    if (!el || expanded) return;
    setShowReadMore(el.scrollHeight > el.clientHeight + 1);
  }, [body, expanded]);

  useLayoutEffect(() => {
    if (expanded) return undefined;

    measureOverflow();
    const raf = requestAnimationFrame(() => measureOverflow());

    const onResize = () => measureOverflow();
    window.addEventListener("resize", onResize);

    let ro;
    if (typeof ResizeObserver !== "undefined" && textRef.current) {
      ro = new ResizeObserver(onResize);
      ro.observe(textRef.current);
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      ro?.disconnect();
    };
  }, [expanded, measureOverflow]);

  return (
    <div className="mcp2-review-card__body">
      <p
        id={bodyId}
        ref={textRef}
        className={
          expanded
            ? "mcp2-review-card__body-text mcp2-review-card__body-text--expanded"
            : "mcp2-review-card__body-text"
        }
      >
        {body}
      </p>
      {showReadMore && !expanded ? (
        <button
          type="button"
          className="mcp2-review-card__read-more"
          aria-expanded={false}
          aria-controls={bodyId}
          onClick={() => setExpanded(true)}
        >
          See more...
        </button>
      ) : null}
    </div>
  );
}

const membershipGroups = [
  {
    title: "DIY Marketing Access",
    icon: "✦",
    items: [
      "Promote your offers in our 100k+ Bay Area mom community",
      "Testimonials, social proof, and visibility support",
      "Group post visibility in topic-specific subgroups",
      "Share your business in curated community spaces",
      "Submit to the Mamahood blog as a featured contributor",
    ],
  },
  {
    title: "Personalized Support",
    icon: "✿",
    items: [
      "Direct email and text access to Heather Anderson",
      "Complimentary 15-min member check-in calls",
      "Real-time answers and referrals",
    ],
  },
  {
    title: "Group Business Growth & Learning",
    icon: "▴",
    items: [
      "Bi-monthly collective coaching calls",
      "Monthly toolbox masterclasses with experts",
      "Weekly live business brainstorms",
      "Full access to toolbox archive: replays, templates, and handouts",
      "Member-exclusive discounts on partner services",
    ],
  },
  {
    title: "Peer Network & Collaboration",
    icon: "❀",
    items: [
      "Access 800+ vetted women entrepreneurs",
      "Unlimited member introductions curated by Heather",
      "Submit guest content to member podcasts or blogs",
      "Invite-only industry subgroups for thought leadership",
    ],
  },
  {
    title: "Member Profile & Rewards",
    icon: "⌘",
    items: [
      "Your own M List profile on our resource directory",
      "Social links, bio, contact info, and brand description",
      "Earn 10% back on any successful referral",
    ],
  },
];

/** Optional per-review viewport controls for collective review cards (hoodie-style vars on the card). */
function reviewCardStyleVars(review) {
  return {
    "--review-image-position": review.imagePosition ?? "50% 40%",
    "--review-image-scale": review.imageScale ?? 1,
    "--review-card-height": review.cardHeight ?? "360px",
  };
}

const reviews = [
  {
    quote:
      "\"The Mamahood Collective has been a game changer for my business and my life.\"",
    body:
      "I've gotten incredible value before from this community. The confidence has come from TOC experiences.",
    author: "Sarah Johnson",
    meta: "E-commerce Founder · Mom of 2",
    image: "/static/img/AmyThompsonPhotography7149.jpg",
    theme: "teal",
    imagePosition: "45% 20%",
    imageScale: 1.08,
    cardHeight: "330px",
  },
  {
    quote:
      "\"The support and guidance have been one of the best things for my business this year.\"",
    body:
      "I’ve only been in the group for about a month, and I’ve already had two high-ticket inquiries and lots of eyes on my website.\n" +
        "I truly think this group is so knowledgeable and uplifting, and having Heather as our leader really sets the tone for how to treat each other and show up for one another.\n" +
        "The support and guidance have been one of the best things for my business this year. I no longer have to sit alone with questions or feel unsure about how to move forward.\n" +
        "Heather has helped me work through areas where I was hitting roadblocks and see things from a completely different perspective.\n" +
        "I even had a client who had gone quiet come back and book with me after I applied some of her guidance. It’s been incredibly helpful for my sales approach and for navigating some of the pitfalls in my inquiry process.\n",
    author: "Amy Thompson",
    meta: "Amy Thompson Photography",
    image: "/static/img/AmyThompsonPhotography7149.jpg",
    theme: "mustard",
    cardHeight: "420px",
  },
  {
    quote:
      "\"I just can't say enough about how much happier I am with this community.\"",
    body:
      "I've had so many moments of doubt and a simple post and the positive support I receive makes being a mom so much easier.",
    author: "Sophia Amazingmama",
    meta: "Mom of 2",
    image: "/static/img/AmyThompsonPhotography7149.jpg",
    theme: "coral",
    cardHeight: "380px",
  },
];

export default function CollectivePage() {
  return (
    <div className="mcp2-site">
      {/* Intro height: optional `style` CSS vars — `--mcp2-intro-min-height` (e.g. "80vh"), `--mcp2-intro-height` (e.g. "600px"). Defaults: auto. */}
      <section className="mcp2-intro">
        <div className="mcp2-container">
          <h1 className="mh-hero__title">WOMEN GO FURTHER FASTER TOGETHER.</h1>
          <h2>THIS IS THE MAMAHOOD COLLECTIVE.</h2>
          <p>
            When you gather up women who value community over competition,
            personal growth as much as business growth, and collaboration over
            going it alone—the collective energy is extraordinary. Because women
            are sooooooo good at lifting each other up and achieving reach on
            their own.
          </p>

          <div className="mcp2-center-actions">
            <Link to="/twentytwentysix/interview" className="mcp2-pill mcp2-pill--coral">
              Schedule Your Interview →
            </Link>
          </div>
        </div>
      </section>

      <section className="mcp2-membership">
        <div className="mcp2-container">
          <h3>WHAT&apos;S INCLUDED IN YOUR MEMBERSHIP</h3>
          <p className="mcp2-membership__lead">
            Every member gets access to our complete foundation benefits.
            Membership tiers vary based on additional 1:1 marketing support
            upgrades.
          </p>

          <div className="mcp2-membership-card">
            {membershipGroups.map((group) => (
              <div className="mcp2-benefit-group" key={group.title}>
                <div className="mcp2-benefit-group__icon">{group.icon}</div>
                <h4>{group.title}</h4>
                <ul>
                  {group.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mcp2-bonus-card">
            <h4>BONUS</h4>
            <p>
              30% off done-for-you services from our Momentum tier are available
              to you anytime at member-exclusive pricing!
            </p>
          </div>

          <div className="mcp2-center-actions">
            <Link to="/twentytwentysix/interview" className="mcp2-pill mcp2-pill--gold">
              Schedule Your Interview →
            </Link>
          </div>
        </div>
      </section>

      <section className="mcp2-reviews">
        <div className="mcp2-container">
          <h3>WHAT OUR MEMBERS SAY</h3>
          <p className="mcp2-reviews__lead">
            Join hundreds of women+ founders who are building thriving
            businesses without going it alone.
          </p>

          <div className="mcp2-review-grid">
            <article
              className="mcp2-review-card mcp2-review-card--wide"
              style={reviewCardStyleVars(reviews[0])}
            >
              <div className="mcp2-review-card__media">
                <img src={reviews[0].image} alt={reviews[0].author} />
              </div>
              <div className={`mcp2-review-card__quote ${reviews[0].theme}`}>
                <h4>{reviews[0].quote}</h4>
                <ExpandableReviewBody body={reviews[0].body} />
                <strong>{reviews[0].author}</strong>
                <span>{reviews[0].meta}</span>
              </div>
            </article>

            <article
              className="mcp2-review-card mcp2-review-card--split"
              style={reviewCardStyleVars(reviews[1])}
            >
              <div className={`mcp2-review-card__quote ${reviews[1].theme}`}>
                <h4>{reviews[1].quote}</h4>
                <ExpandableReviewBody body={reviews[1].body} />
                <strong>{reviews[1].author}</strong>
                <span>{reviews[1].meta}</span>
              </div>
              <div className="mcp2-review-card__media">
                <img src={reviews[1].image} alt={reviews[1].author} />
              </div>
            </article>

            <article
              className="mcp2-review-card mcp2-review-card--wide reverse"
              style={reviewCardStyleVars(reviews[2])}
            >
              <div className="mcp2-review-card__media">
                <img src={reviews[2].image} alt={reviews[2].author} />
              </div>
              <div className={`mcp2-review-card__quote ${reviews[2].theme}`}>
                <h4>{reviews[2].quote}</h4>
                <ExpandableReviewBody body={reviews[2].body} />
                <strong>{reviews[2].author}</strong>
                <span>{reviews[2].meta}</span>
              </div>
            </article>
          </div>

          <Link className="mcp2-more-link" to="/twentytwentysix/reviews">
            More Reviews →
          </Link>
        </div>
      </section>
    </div>
  );
}
